import React, { useEffect, useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { ChatRoom } from '../../contexts/ChatContext';

interface ChatListProps {
  onSelectChat: (errandId: string) => void;
  selectedErrandId?: string;
}

const ChatList: React.FC<ChatListProps> = ({ onSelectChat, selectedErrandId }) => {
  const { chatRooms, fetchChatRooms, loading, error } = useChat();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchChatRooms();
  }, [fetchChatRooms]);

  // Filter chat rooms based on search term
  const filteredRooms = chatRooms.filter(room =>
    room.errand_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (room.last_message?.message || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format last message preview
  const formatMessagePreview = (message: string) => {
    if (message.length > 50) {
      return message.substring(0, 50) + '...';
    }
    return message;
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get user role for display
  const getUserRole = (room: ChatRoom) => {
    // Get user ID from JWT token
    const token = localStorage.getItem('token');
    if (!token) return 'Unknown';

    // For now, we'll determine role based on the room structure
    // This is a simplified approach - in a real app you'd decode the JWT
    if (room.user_id && room.driver_id) {
      // If both user_id and driver_id exist, we need to determine which one is current user
      // For now, let's assume if driver_id exists, current user is driver
      return room.driver_id ? 'Driver' : 'Customer';
    }
    return 'Customer';
  };

  if (loading && chatRooms.length === 0) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
        {error}
      </div>
    );
  }

  if (chatRooms.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <div className="w-16 h-16 mx-auto mb-3 text-gray-300">
          <svg fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
          </svg>
        </div>
        <p>No conversations yet</p>
        <p className="text-sm">Start an errand to begin chatting!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search conversations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Chat Rooms List */}
      <div className="space-y-2">
        {filteredRooms.map((room) => (
          <div
            key={room.errand_id}
            onClick={() => onSelectChat(room.errand_id)}
            className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
              selectedErrandId === room.errand_id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="font-medium text-gray-900 truncate">
                    {room.errand_title}
                  </h4>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getUserRole(room)}
                  </span>
                </div>

                {room.last_message ? (
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600 truncate">
                      {formatMessagePreview(room.last_message.message)}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{room.last_message.sender_name}</span>
                      <span>{formatTimestamp(room.last_message.created_at)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No messages yet</p>
                )}
              </div>

              {/* Unread count indicator */}
              {room.unread_count > 0 && (
                <div className="ml-3 flex-shrink-0">
                  <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                    {room.unread_count > 99 ? '99+' : room.unread_count}
                  </span>
                </div>
              )}
            </div>

            {/* Status indicator */}
            <div className="mt-2 flex items-center space-x-2 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span>Active</span>
              </div>
              <span>•</span>
              <span>{formatTimestamp(room.updated_at)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* No search results */}
      {searchTerm && filteredRooms.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          <p>No conversations found</p>
          <p className="text-sm">Try adjusting your search terms</p>
        </div>
      )}
    </div>
  );
};

export default ChatList;
